import type { CSSObject } from "type/tss";
import { Theme } from "@mui/material/styles";

// Measurements are from the IDX page Figma (VPLAT 1, `2551:9599`): 24px between Connection rows,
// and 4px from a row's label to its value.
export const styles = (theme: Theme) => {
	return ({
		connectionRows: {
			display: "flex",
			flexDirection: "column",
			alignItems: "flex-start",
			gap: theme.spacing(3),
		},
		connectionRow: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(0.5),
		},
		// VPLAT 15's banner: the full width of the block, 16 padding, radius 4, icon and copy 8 apart,
		// in the same warning surface as the setup callouts.
		setupBanner: {
			alignSelf: "stretch",
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(1),
			padding: theme.spacing(2),
			borderRadius: 4,
			backgroundColor: theme.palette.custom.warningLight,
		},
		setupBannerIcon: {
			flexShrink: 0,
			color: theme.palette.custom.tagWarningText,
		},
		setupBannerText: {
			flex: 1,
			fontSize: 16,
			lineHeight: "24px",
			color: theme.palette.custom.textPrimary,
		},
		// The WordPress instruction sits 16 above the key it describes, closer than the 24 between rows.
		keyGroup: {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(2),
		},
		rowInstruction: {
			fontSize: 16,
			lineHeight: "24px",
			color: theme.palette.custom.textPrimary,
		},
		// MAX-4506 sets the WordPress instruction at Body1 (18), the size of the values it sits among.
		keyInstruction: {
			fontSize: 18,
			lineHeight: "24px",
			color: theme.palette.custom.textPrimary,
		},
		// The full width of the block, between the platform rows and the key, in the same 24 row gap.
		divider: {
			alignSelf: "stretch",
			borderColor: theme.palette.custom.border,
		},
		// Label to button is 8, the design kit's label-to-field gap.
		setupRow: {
			display: "flex",
			flexDirection: "column",
			alignItems: "flex-start",
			gap: theme.spacing(1),
		},
		rowLabel: {
			fontSize: 16,
			lineHeight: "24px",
			color: theme.palette.text.secondary,
		},
		rowValue: {
			display: "flex",
			alignItems: "center",
			flexWrap: "wrap",
			gap: theme.spacing(1),
		},
		rowText: {
			fontSize: 18,
			lineHeight: "24px",
			color: theme.palette.custom.textPrimary,
			wordBreak: "break-all",
		},
		// The Website URL editor (MAX-4576): the input and Done side by side, as the custom slug's editor.
		// The row takes the block's width; items sit at the top so an error under the input doesn't drag Done down.
		urlEditRow: {
			alignSelf: "stretch",
			display: "flex",
			alignItems: "flex-start",
			gap: theme.spacing(1),
		},
		// Editing, the row spans the block so the input can take the design's width.
		connectionRowEditing: {
			alignSelf: "stretch",
		},
		urlInput: {
			flex: "0 1 546px",
			minWidth: 0,
			// Gray focus, not the shared orange, as on the custom slug field (Dave's call there): orange
			// reads as an error beside a real (red) error state.
			"& .Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
				borderColor: theme.palette.custom.textSecondary,
			},
			"& .Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
				borderColor: theme.palette.error.main,
			},
		},
		doneButton: {
			flexShrink: 0,
			height: 48,
		},
		platform: {
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(1),
			color: theme.palette.custom.textPrimary,
		},
		platformLabel: {
			fontSize: 16,
			lineHeight: "24px",
			color: theme.palette.custom.textPrimary,
		},
		// Figma's tertiary button: teal text with a 16px icon, no padding of its own, so it sits on
		// the same line as the value it acts on.
		inlineAction: {
			minWidth: 0,
			padding: 0,
			fontSize: 16,
			lineHeight: "20px",
			color: theme.palette.custom.tertiaryAction,
			"& .MuiButton-startIcon": {
				marginLeft: 0,
				marginRight: theme.spacing(0.5),
			},
			"& .MuiButton-endIcon": {
				marginLeft: theme.spacing(0.5),
				marginRight: 0,
			},
			"&:hover": {
				backgroundColor: "transparent",
				textDecoration: "underline",
			},
		},
	} satisfies Record<string, CSSObject>);
};
